import React, { useState, useEffect } from 'react';
import {
    Modal,
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    Platform,
    ActivityIndicator,
    Linking,
    Animated,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import { supabase, toError } from '../../lib/supabase';
import { getDigestUrl } from '../../lib/paperUrl';
import { DigestIssue } from '../../lib/hooks/useDigestQuery';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold, interMedium, interRegular } from '../../lib/fonts';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { type LocalizedMessage, useOptionalResolvedMessage } from '../../lib/i18n/messages';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';
import { localizedPublicUrl } from '../../lib/i18n/publicWeb';

// Add CSS animations for web
if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        @keyframes shareModalSlideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shareModalFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes copySuccess {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
        }
        .share-modal-content {
            animation: shareModalSlideUp 0.3s ease-out;
        }
        .share-modal-overlay {
            animation: shareModalFadeIn 0.2s ease-out;
        }
        .copy-success {
            animation: copySuccess 0.3s ease-out;
        }
        .social-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .social-button {
            transition: all 0.2s ease;
        }
        .copy-button:hover {
            filter: brightness(1.05);
        }
        .copy-button {
            transition: all 0.2s ease;
        }
    `;
    document.head.appendChild(style);
}

interface ShareDigestModalProps {
    visible: boolean;
    onClose: () => void;
    digestIssue: DigestIssue;
    isOwner: boolean;
    onVisibilityChanged?: () => void;
}

export default function ShareDigestModal({
    visible,
    onClose,
    digestIssue,
    isOwner,
    onVisibilityChanged,
}: ShareDigestModalProps) {
    const { messageLocale } = useAppLocale();
    const { fontsLoaded } = useFontLoading();
    const [isPublic, setIsPublic] = useState(digestIssue.project.visibility === 'public');
    const [isMakingPublic, setIsMakingPublic] = useState(false);
    const [makePublicError, setMakePublicError] = useState<LocalizedMessage | null>(null);
    const resolvedMakePublicError = useOptionalResolvedMessage(makePublicError);
    const [copyFeedback, setCopyFeedback] = useState(false);

    // Sync isPublic state when digestIssue changes
    useEffect(() => {
        setIsPublic(digestIssue.project.visibility === 'public');
    }, [digestIssue.project.visibility]);

    const shareUrl = localizedPublicUrl(getDigestUrl(digestIssue), messageLocale, {
        allowTestLocale: messageLocale === 'en-XA',
    });

    const handleCopyLink = async () => {
        if (Platform.OS === 'web') {
            await navigator.clipboard.writeText(shareUrl);
        } else {
            // For mobile, use expo-clipboard
            const Clipboard = require('expo-clipboard');
            await Clipboard.setStringAsync(shareUrl);
        }

        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
    };

    const handleMakePublic = async () => {
        setIsMakingPublic(true);
        setMakePublicError(null);

        try {
            const { error } = await supabase
                .from('project')
                .update({ visibility: 'public' })
                .eq('id', digestIssue.project.id);

            if (error) throw toError(error);

            setIsPublic(true);
            onVisibilityChanged?.();
        } catch (err: any) {
            console.error('[ShareDigestModal] Could not make digest public:', err);
            setMakePublicError(msg({ id: 'share.digest.makePublicFailure', message: 'Failed to make digest public. Please try again.' }));
        } finally {
            setIsMakingPublic(false);
        }
    };

    const digestName = digestIssue.project.name;

    const handleTwitterShare = () => {
        const encodedUrl = encodeURIComponent(shareUrl);
        const text = encodeURIComponent(`Check out "${digestName}" - a research digest on Paperzilla`);
        const twitterUrl = `https://x.com/intent/tweet?text=${text}&url=${encodedUrl}`;

        if (Platform.OS === 'web') {
            window.open(twitterUrl, '_blank', 'width=600,height=400');
        } else {
            Linking.openURL(twitterUrl);
        }
    };

    const handleLinkedInShare = () => {
        const encodedUrl = encodeURIComponent(shareUrl);
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

        if (Platform.OS === 'web') {
            window.open(linkedinUrl, '_blank', 'width=600,height=600');
        } else {
            Linking.openURL(linkedinUrl);
        }
    };

    const handleEmailShare = () => {
        const subject = encodeURIComponent(`Research digest: ${digestName}`);
        const body = encodeURIComponent(`I thought you might find this research digest interesting:\n\n${digestName}\n\n${shareUrl}`);
        const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;

        Linking.openURL(mailtoUrl);
    };

    const renderPublicContent = () => (
        <View
            style={styles.contentContainer}
            {...(Platform.OS === 'web' ? { className: 'share-modal-content' } : {})}
        >
            {/* Hero section with digest name */}
            <View style={styles.heroSection}>
                <View style={styles.shareIconContainer}>
                    <FontAwesome6 name="share-from-square" size={24} color={colors.primaryGreen} />
                </View>
                <Text style={[styles.digestNameText, fontsLoaded && interMedium]} numberOfLines={2}>
                    {digestName}
                </Text>
            </View>

            {/* URL Display */}
            <View style={[styles.urlContainer, copyFeedback && styles.urlContainerSuccess]}>
                <TextInput
                    style={[styles.urlInput, fontsLoaded && interRegular]}
                    value={shareUrl}
                    editable={false}
                    selectTextOnFocus
                />
                <TouchableOpacity
                    style={[styles.copyIconButton, copyFeedback && styles.copyIconButtonSuccess]}
                    onPress={handleCopyLink}
                    activeOpacity={0.7}
                >
                    <FontAwesome6
                        name={copyFeedback ? 'check' : 'copy'}
                        size={16}
                        color={copyFeedback ? '#ffffff' : colors.text600}
                    />
                </TouchableOpacity>
            </View>

            {/* Copy Button */}
            <TouchableOpacity
                style={[styles.copyButton, copyFeedback && styles.copyButtonSuccess]}
                onPress={handleCopyLink}
                activeOpacity={0.8}
                {...(Platform.OS === 'web' ? { className: `copy-button ${copyFeedback ? 'copy-success' : ''}` } : {})}
            >
                {copyFeedback ? (
                    <View style={styles.copyButtonContent}>
                        <FontAwesome6 name="check" size={16} color="#ffffff" />
                        <Text style={[styles.copyButtonText, fontsLoaded && interSemiBold]}><Trans id="digest.sharedigestmodal.copiedToClipboard" comment="UI copy in components/digest/ShareDigestModal.tsx">
                            Copied to clipboard!
                        </Trans></Text>
                    </View>
                ) : (
                    <View style={styles.copyButtonContent}>
                        <FontAwesome6 name="link" size={14} color="#ffffff" />
                        <Text style={[styles.copyButtonText, fontsLoaded && interSemiBold]}><Trans id="digest.sharedigestmodal.copyLink" comment="UI copy in components/digest/ShareDigestModal.tsx">
                            Copy Link
                        </Trans></Text>
                    </View>
                )}
            </TouchableOpacity>

            {/* Divider */}
            <View style={styles.dividerContainer}>
                <View style={styles.dividerLine} />
                <Text style={[styles.dividerText, fontsLoaded && interRegular]}><Trans id="digest.sharedigestmodal.orShareDirectly" comment="UI copy in components/digest/ShareDigestModal.tsx">
                    or share directly
                </Trans></Text>
                <View style={styles.dividerLine} />
            </View>

            {/* Social Share Buttons */}
            <View style={styles.socialButtonsRow}>
                <TouchableOpacity
                    style={[styles.socialButton, styles.twitterButton]}
                    onPress={handleTwitterShare}
                    activeOpacity={0.8}
                    {...(Platform.OS === 'web' ? { className: 'social-button' } : {})}
                >
                    <FontAwesome6 name="x-twitter" size={18} color="#ffffff" />
                    <Text style={[styles.socialButtonText, styles.socialButtonTextLight, fontsLoaded && interMedium]}><Trans id="digest.sharedigestmodal.post" comment="UI copy in components/digest/ShareDigestModal.tsx">
                        Post
                    </Trans></Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.socialButton, styles.linkedinButton]}
                    onPress={handleLinkedInShare}
                    activeOpacity={0.8}
                    {...(Platform.OS === 'web' ? { className: 'social-button' } : {})}
                >
                    <FontAwesome6 name="linkedin-in" size={18} color="#ffffff" />
                    <Text style={[styles.socialButtonText, styles.socialButtonTextLight, fontsLoaded && interMedium]}><Trans id="digest.sharedigestmodal.share" comment="UI copy in components/digest/ShareDigestModal.tsx">
                        Share
                    </Trans></Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.socialButton, styles.emailButton]}
                    onPress={handleEmailShare}
                    activeOpacity={0.8}
                    {...(Platform.OS === 'web' ? { className: 'social-button' } : {})}
                >
                    <FontAwesome6 name="envelope" size={18} color="#ffffff" />
                    <Text style={[styles.socialButtonText, styles.socialButtonTextLight, fontsLoaded && interMedium]}><Trans id="digest.sharedigestmodal.email" comment="UI copy in components/digest/ShareDigestModal.tsx">
                        Email
                    </Trans></Text>
                </TouchableOpacity>
            </View>
        </View>
    );

    const renderPrivateContent = () => (
        <View
            style={styles.contentContainer}
            {...(Platform.OS === 'web' ? { className: 'share-modal-content' } : {})}
        >
            <View style={styles.privatePrompt}>
                <View style={styles.privateIconContainer}>
                    <FontAwesome6 name="lock" size={26} color="#d97706" />
                </View>
                <Text style={[styles.privateTitle, fontsLoaded && interSemiBold]}><Trans id="digest.sharedigestmodal.thisDigestIsPrivate" comment="UI copy in components/digest/ShareDigestModal.tsx">
                    This digest is private
                </Trans></Text>
                <Text style={[styles.privateDescription, fontsLoaded && interRegular]}><Trans id="digest.sharedigestmodal.makeItPublicToShareWithAnyoneTheyLlBeAble" comment="UI copy in components/digest/ShareDigestModal.tsx">
                    Make it public to share with anyone. They'll be able to view this and future digest issues.
                </Trans></Text>

                {resolvedMakePublicError && (
                    <View style={styles.errorContainer}>
                        <FontAwesome6 name="circle-exclamation" size={16} color={colors.errorRed} />
                        <Text style={[styles.errorText, fontsLoaded && interRegular]}>
                            {resolvedMakePublicError}
                        </Text>
                        <TouchableOpacity onPress={handleMakePublic}>
                            <Text style={[styles.retryText, fontsLoaded && interSemiBold]}><Trans id="digest.sharedigestmodal.retry" comment="UI copy in components/digest/ShareDigestModal.tsx">
                                Retry
                            </Trans></Text>
                        </TouchableOpacity>
                    </View>
                )}

                <TouchableOpacity
                    style={[styles.makePublicButton, isMakingPublic && styles.makePublicButtonDisabled]}
                    onPress={handleMakePublic}
                    activeOpacity={0.8}
                    disabled={isMakingPublic}
                    {...(Platform.OS === 'web' ? { className: 'copy-button' } : {})}
                >
                    {isMakingPublic ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                        <View style={styles.copyButtonContent}>
                            <FontAwesome6 name="globe" size={15} color="#ffffff" />
                            <Text style={[styles.makePublicButtonText, fontsLoaded && interSemiBold]}><Trans id="digest.sharedigestmodal.makePublicShare" comment="UI copy in components/digest/ShareDigestModal.tsx">
                                Make Public & Share
                            </Trans></Text>
                        </View>
                    )}
                </TouchableOpacity>
            </View>
        </View>
    );

    // Non-owner viewing private digest shouldn't see anything (they can't access the page anyway)
    // But if they somehow got here, show nothing useful
    const showPrivatePrompt = !isPublic && isOwner;
    const showPublicContent = isPublic;

    return (
        <Modal
            visible={visible}
            animationType="slide"
            presentationStyle="pageSheet"
            onRequestClose={onClose}
        >
            <View style={styles.modalContainer}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={[styles.headerTitle, fontsLoaded && interSemiBold]}><Trans id="digest.sharedigestmodal.shareThisDigest" comment="UI copy in components/digest/ShareDigestModal.tsx">
                        Share this digest
                    </Trans></Text>
                    <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                        <FontAwesome6 name="xmark" size={20} color={colors.text700} />
                    </TouchableOpacity>
                </View>

                {/* Content */}
                {showPublicContent && renderPublicContent()}
                {showPrivatePrompt && renderPrivateContent()}
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    modalContainer: {
        flex: 1,
        backgroundColor: '#f8fafc',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 24,
        paddingTop: 20,
        paddingBottom: 16,
        backgroundColor: 'white',
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        ...(Platform.OS === 'web' && {
            maxWidth: 480,
            alignSelf: 'center',
            width: '100%',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            marginTop: 60,
        }),
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: '600',
        color: colors.text900,
        letterSpacing: -0.3,
    },
    closeButton: {
        padding: 8,
        marginRight: -8,
        borderRadius: 20,
        backgroundColor: '#f3f4f6',
    },
    contentContainer: {
        padding: 24,
        paddingTop: 20,
        backgroundColor: 'white',
        ...(Platform.OS === 'web' && {
            maxWidth: 480,
            alignSelf: 'center',
            width: '100%',
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            paddingBottom: 28,
        }),
    },
    heroSection: {
        alignItems: 'center',
        marginBottom: 24,
    },
    shareIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: colors.primary100,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
    },
    digestNameText: {
        fontSize: 15,
        color: colors.text700,
        textAlign: 'center',
        lineHeight: 22,
        maxWidth: 320,
    },
    urlContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        borderRadius: 10,
        borderWidth: 1.5,
        borderColor: '#e2e8f0',
        marginBottom: 12,
        overflow: 'hidden',
    },
    urlContainerSuccess: {
        borderColor: colors.primaryGreen,
        backgroundColor: '#f0fdf4',
    },
    urlInput: {
        flex: 1,
        paddingHorizontal: 14,
        paddingVertical: 13,
        fontSize: 13,
        color: colors.text600,
        ...(Platform.OS === 'web' && {
            outlineStyle: 'none' as any,
        }),
    },
    copyIconButton: {
        padding: 14,
        backgroundColor: '#f1f5f9',
        borderLeftWidth: 1,
        borderLeftColor: '#e2e8f0',
    },
    copyIconButtonSuccess: {
        backgroundColor: colors.primaryGreen,
        borderLeftColor: colors.primaryGreen,
    },
    copyButton: {
        backgroundColor: colors.primaryGreen,
        borderRadius: 10,
        paddingVertical: 14,
        alignItems: 'center',
        marginBottom: 24,
        ...(Platform.OS !== 'web' && {
            shadowColor: colors.primaryGreen,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.2,
            shadowRadius: 8,
            elevation: 4,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 4px 8px rgba(22,163,74,0.2)',
        }),
    },
    copyButtonSuccess: {
        backgroundColor: '#16a34a',
    },
    copyButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    copyButtonText: {
        color: '#ffffff',
        fontSize: 15,
        fontWeight: '600',
        letterSpacing: -0.2,
    },
    dividerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 20,
    },
    dividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: '#e2e8f0',
    },
    dividerText: {
        marginHorizontal: 14,
        fontSize: 13,
        color: colors.text500,
        textTransform: 'lowercase',
    },
    socialButtonsRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 10,
    },
    socialButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 11,
        paddingHorizontal: 18,
        borderRadius: 10,
        minWidth: 100,
    },
    twitterButton: {
        backgroundColor: '#000000',
    },
    linkedinButton: {
        backgroundColor: '#0A66C2',
    },
    emailButton: {
        backgroundColor: colors.text700,
    },
    socialButtonText: {
        fontSize: 14,
        fontWeight: '500',
    },
    socialButtonTextLight: {
        color: '#ffffff',
    },
    privatePrompt: {
        alignItems: 'center',
        paddingVertical: 20,
    },
    privateIconContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: '#fef3c7',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    privateTitle: {
        fontSize: 17,
        fontWeight: '600',
        color: colors.text900,
        marginTop: 8,
        marginBottom: 8,
        textAlign: 'center',
        letterSpacing: -0.3,
    },
    privateDescription: {
        fontSize: 14,
        color: colors.text600,
        textAlign: 'center',
        lineHeight: 22,
        marginBottom: 24,
        maxWidth: 300,
    },
    errorContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 16,
        padding: 12,
        backgroundColor: '#fef2f2',
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#fecaca',
    },
    errorText: {
        flex: 1,
        fontSize: 14,
        color: colors.errorRed,
    },
    retryText: {
        fontSize: 14,
        color: colors.primaryGreen,
        fontWeight: '600',
    },
    makePublicButton: {
        backgroundColor: colors.primaryGreen,
        borderRadius: 10,
        paddingVertical: 14,
        paddingHorizontal: 28,
        minWidth: 220,
        alignItems: 'center',
        ...(Platform.OS !== 'web' && {
            shadowColor: colors.primaryGreen,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.2,
            shadowRadius: 8,
            elevation: 4,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 4px 8px rgba(22,163,74,0.2)',
        }),
    },
    makePublicButtonDisabled: {
        opacity: 0.7,
    },
    makePublicButtonText: {
        color: '#ffffff',
        fontSize: 15,
        fontWeight: '600',
        letterSpacing: -0.2,
    },
});
